import { Grid, makeStyles } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SearchSelect from 'react-select';
import _isEmpty from 'lodash/isEmpty';
import Theme from '../ui/Theme';
import service from '../../apis/service';
import {
  getAssignedJourneys,
  getDepartment,
  getEmployeeList,
  getMandatoryJourneys,
  getSelectedRoleID,
  showProgress,
} from '../../redux/actions/DealershipActions';
import EmployeeJourneyTabContent from './EmployeeJourneyTabContent';
import { apiEndpoints } from '../../constants/apiEndpoints';

const useStyles = makeStyles(() => ({
  selectTopBar: {
    border: `1px solid ${Theme.palette.secondary.main}`,
    borderRadius: '0',
    height: '50px',
    width: 'inherit',
  },
  smallRadioButton: {
    '& svg': {
      width: '0.8em',
      height: '0.8em',
    },
  },
  radioButton: {
    height: '5px',
    width: '5px',
  },
  assignToText: {
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '12px',
    lineHeight: '140%',
    marginRight: '20px',
    color: 'rgba(0, 0, 0, 0.4)',
    textTransform: 'uppercase',
  },
  form: {
    marginLeft: '20px',
    marginTop: '10px',
  },
}));

export default function DepartmentDealershipContent() {
  const classes = useStyles();

  const dispatch = useDispatch();

  const dealershipID = useSelector((state) => state.getDealershipID);
  const dealershipDepartmentValues = useSelector((state) => state.getDealershipDepartments.dealershipDepartmentValues);

  const departmentID = useSelector((state) => state.getDepartment);
  const departmentRoleID = useSelector((state) => state.getSelectedRoleID);

  const [selectedDepartmentValue, setSelectedDepartmentValue] = useState({});
  const [selectedDepartmentRoleValue, setSelectedDepartmentRoleValue] = useState({});

  const [tabValue, setTabValue] = useState(0);

  const fetchJourneys = useCallback(
    async (dealershipId, department, departmentRole) => {
      dispatch(showProgress(true));
      await service
        .get(
          apiEndpoints.DEALER_DEP_JOURNEYS.replace('DEALER_ID', dealershipId)
            .replace('DEP_ID', department)
            .replace('ROLE_ID', departmentRole),
        )
        .then((response) => {
          const mandJourneys = [];
          dispatch(getAssignedJourneys(response));
          response?.forEach((data1) => {
            if (data1.mandatory === true) {
              mandJourneys.push(data1.id);
            }
          });
          dispatch(getMandatoryJourneys('add', mandJourneys));
          dispatch(showProgress(false));
        })
        .catch((error) => {
          console.log(error);
          dispatch(showProgress(false));
        });
    },
    [dispatch],
  );

  useEffect(() => {
    if (tabValue) {
      fetchJourneys(dealershipID, departmentID, departmentRoleID);
    }
  }, [dealershipID, departmentID, departmentRoleID, fetchJourneys, tabValue]);

  useEffect(() => {
    if (!_isEmpty(dealershipDepartmentValues)) {
      setSelectedDepartmentValue({
        id: dealershipDepartmentValues[0].id,
        name: dealershipDepartmentValues[0].name,
      });
      setSelectedDepartmentRoleValue({
        id: dealershipDepartmentValues[0].roles[0].id,
        name: dealershipDepartmentValues[0].roles[0].name,
      });
    }
  }, [dealershipDepartmentValues]);

  useEffect(() => {
    const val = dealershipDepartmentValues?.find((i) => i.id === selectedDepartmentValue.id)?.roles[0];
    if (!_isEmpty(val)) {
      setSelectedDepartmentRoleValue({
        id: val.id,
        name: val.name,
      });
      dispatch(getDepartment(selectedDepartmentValue.id));
      dispatch(getSelectedRoleID(val.id));
    }
  }, [selectedDepartmentValue]);

  const fetchEmployees = useCallback(
    async (dealershipId, department, departmentRole) => {
      dispatch(showProgress(true));

      await service
        .get(
          apiEndpoints.DEALER_DEP_EMPLOYEES.replace('DEALER_ID', dealershipId)
            .replace('DEP_ID', department)
            .replace('ROLE_ID', departmentRole),
        )
        .then((response) => {
          dispatch(getEmployeeList('fetch', response));
          dispatch(showProgress(false));
        })
        .catch((error) => {
          console.log(error);
          dispatch(showProgress(false));
        });
    },
    [dispatch],
  );

  useEffect(() => {
    if (dealershipID) {
      fetchEmployees(dealershipID, departmentID, departmentRoleID);
      // fetchJourneys(dealershipID, departmentID, departmentRoleID);
    }
  }, [departmentID, departmentRoleID, dealershipID, fetchEmployees]);

  const optionsDepartmentRoles = useMemo(
    () => dealershipDepartmentValues?.find((i) => i.id === selectedDepartmentValue.id)?.roles || [],
    [dealershipDepartmentValues, selectedDepartmentValue],
  );

  return (
    <>
      <Grid container direction="column" className={classes.selectTopBar} justifyContent="space-between">
        <Grid xs={4} item style={{ marginLeft: '10px', marginTop: '5px', width: '100%' }}>
          <SearchSelect
            placeholder="Select Department"
            value={selectedDepartmentValue}
            onChange={(val) => {
              setSelectedDepartmentValue(val);
              if (val.id !== selectedDepartmentValue.id) dispatch(showProgress(true));
            }}
            options={dealershipDepartmentValues || []}
            maxMenuHeight="200px"
            styles={{
              control: (provided) => ({
                ...provided,
                backgroundColor: '#f4f4f4',
              }),
            }}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isClearable={false}
          />
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid xs={6} item style={{ marginRight: '10px', marginTop: '5px', width: '100%' }}>
          <SearchSelect
            placeholder="Select Department Role"
            value={selectedDepartmentRoleValue}
            onChange={(val) => {
              setSelectedDepartmentRoleValue(val);
              dispatch(getSelectedRoleID(val.id));
              if (val.id !== selectedDepartmentRoleValue.id) dispatch(showProgress(true));
            }}
            options={optionsDepartmentRoles}
            maxMenuHeight="200px"
            styles={{
              control: (provided) => ({
                ...provided,
                backgroundColor: '#f4f4f4',
              }),
            }}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isClearable={false}
          />
        </Grid>
      </Grid>
      <EmployeeJourneyTabContent
        tabValue={tabValue}
        setTabValue={setTabValue}
        selectedDep={selectedDepartmentValue}
        selectedRole={selectedDepartmentRoleValue}
        refreshData={(type) => {
          if (type === 'employees') fetchEmployees(dealershipID, departmentID, departmentRoleID);
          else fetchJourneys(dealershipID, departmentID, departmentRoleID);
        }}
        showJourneyAddIcon
        flow="department"
      />
    </>
  );
}
